import { useFeature, WebLink } from '@news-mono/web-common'
import React from 'react'
import { useLocation } from 'react-router'
import {
    StyledBackgroundWrap,
    StyledCNBCLogo,
    StyledCommonwealthGamesContainer,
    StyledCommonwealthGamesText,
    StyledFeatureBorder,
    StyledFeatureContainer,
    StyledFeatureSectionHeader,
    StyledFeatureSectionHeaderContainer,
    StyledFreoTag,
    StyledGradientSectionTitle,
    StyledGradientSectionTitleH1,
    StyledGradientWrap,
    StyledGrandFinalSectionHeader,
    StyledGrandFinalText,
    StyledGrandFinalWebLink,
    StyledIconArrowCircle,
    StyledPollieRaterBanner,
    StyledPollieRaterBannerButton,
    StyledPollieRaterBannerTitle,
    StyledPollieRaterBannerWrap,
    StyledThor,
    StyledTitleHeader,
} from '../../compositions/Thor/Thor.styled'
import { QueensLegacyLogo } from '../../logos/QueensLegacy/QueensLegacyLogo'
import { Breadcrumb, LinkProps } from '../../navigation'
import { ThemeOverrider } from '../../themes/ThemeOverrider/ThemeOverrider'
import { ContainerWidth, ThemeMargins } from '../../__styling/settings/metrics'
import { ThemeOverrideTypes } from '../../__styling/themes'

export interface ThorProps {
    mainContent: React.ReactElement<any>
    hasBackgroundFill?: boolean
    /** If true or undefined, will default to the outer margin of the theme */
    horizontalGutters?: boolean | keyof ThemeMargins
    /** If true or undefined, will default to the outer margin of the theme */
    verticalGutters?: boolean | keyof ThemeMargins
    verticalSpacing?: keyof ThemeMargins
    containerWidth?: ContainerWidth
    themeOverride?: ThemeOverrideTypes
    hideHeadingLink?: boolean
    // Pollie Rater / Federal Election specific props
    hidePollieRaterBanner?: boolean
    isHeadingH1?: boolean
    // {TODO} Move these from apps/thewest/src/app/routes/home/get-themed-segment.ts and into component-library
    layouts?: '4-card' | '8-card' | '7-card'
    hasBorder?: boolean
    // {TODO} Discuss if this should be retained
    unsetFeatureContainerBackground?: boolean
}

export const Thor: React.FC<ThorProps> = ({
    mainContent,
    hasBackgroundFill,
    horizontalGutters,
    verticalGutters,
    verticalSpacing,
    containerWidth,
    themeOverride,
    hideHeadingLink,
    hidePollieRaterBanner,
    isHeadingH1,
    layouts,
    hasBorder,
    unsetFeatureContainerBackground,
}) => {
    const styledThor = (
        <StyledThor
            hasBackgroundFill={hasBackgroundFill}
            horizontalGutters={horizontalGutters}
            verticalGutters={verticalGutters}
            verticalSpacing={verticalSpacing}
            containerWidth={containerWidth || 1020}
        >
            {mainContent}
        </StyledThor>
    )

    const location = useLocation()

    const freoWon = themeOverride === 'grand-final-freo'

    const showPollieRaterBanner =
        useFeature('pollie-rater-drivers') && !hidePollieRaterBanner

    if (
        themeOverride === 'grand-final' ||
        themeOverride === 'grand-final-freo'
    ) {
        return (
            <ThemeOverrider override={themeOverride}>
                <StyledBackgroundWrap themeOverride={themeOverride}>
                    <StyledFeatureContainer
                        initialFlexDirection={'row'}
                        customPadding={16}
                        freoWon={freoWon}
                        hasExtraBottomMargin={true}
                        layout={layouts}
                        unsetFeatureContainerBackground={
                            unsetFeatureContainerBackground
                        }
                    >
                        <StyledTitleHeader>
                            <StyledGrandFinalText>
                                AFL Finals 2022
                            </StyledGrandFinalText>
                            {freoWon && (
                                <StyledFreoTag>#flagmantle</StyledFreoTag>
                            )}
                        </StyledTitleHeader>
                        {!hideHeadingLink && (
                            <div>
                                <StyledGrandFinalWebLink to={'/sport/afl'}>
                                    <StyledGrandFinalSectionHeader>
                                        More Coverage
                                    </StyledGrandFinalSectionHeader>
                                    <StyledIconArrowCircle />
                                </StyledGrandFinalWebLink>
                            </div>
                        )}
                    </StyledFeatureContainer>
                    {styledThor}
                </StyledBackgroundWrap>
            </ThemeOverrider>
        )
    }

    if (themeOverride === 'queens-legacy') {
        return (
            <ThemeOverrider override={themeOverride}>
                <StyledFeatureContainer>
                    <WebLink to="/news/queen-elizabeth-ii">
                        <QueensLegacyLogo logoWidth={400} />
                    </WebLink>

                    <StyledFeatureSectionHeader
                        heading="More Stories"
                        headingUrl="/news/queen-elizabeth-ii"
                        showChevron={true}
                    />
                </StyledFeatureContainer>
                {styledThor}
            </ThemeOverrider>
        )
    }

    if (themeOverride === 'cnbc') {
        //We don't want to display anything if we don't have any content.
        if (!mainContent) {
            return (
                <ThemeOverrider override={themeOverride}>
                    {styledThor}
                </ThemeOverrider>
            )
        }

        const CNBCLogoSource = require('../../logos/CNBCLogos/CNBC_Logo_White.png')

        return (
            <ThemeOverrider override={themeOverride}>
                <StyledFeatureContainer>
                    <WebLink to="/business/cnbc">
                        <StyledCNBCLogo src={CNBCLogoSource} />
                    </WebLink>
                    <StyledFeatureSectionHeader
                        heading="More stories"
                        headingUrl="/business/cnbc"
                        showChevron
                    />
                </StyledFeatureContainer>
                {styledThor}
            </ThemeOverrider>
        )
    }

    if (themeOverride === 'commonwealth-games') {
        if (layouts === '4-card') {
            return (
                <ThemeOverrider override={themeOverride}>
                    <StyledFeatureContainer hasExtraBottomMargin={true}>
                        <StyledCommonwealthGamesContainer>
                            <StyledCommonwealthGamesText>
                                COMMONWEALTH &nbsp;
                                <br />
                                GAMES 2022
                            </StyledCommonwealthGamesText>
                        </StyledCommonwealthGamesContainer>
                        <StyledFeatureSectionHeaderContainer>
                            <StyledFeatureSectionHeader
                                heading="Full Coverage"
                                headingUrl="/sport/commonwealth-games"
                                showChevron
                                colorOverride={'#fff'}
                            />
                        </StyledFeatureSectionHeaderContainer>
                    </StyledFeatureContainer>
                    {styledThor}
                </ThemeOverrider>
            )
        } else if (layouts === '7-card') {
            const commonwealthGamesHeaders: LinkProps[] = [
                {
                    text: 'Sport',
                    href: '/sport',
                },
                {
                    text: 'Commonwealth Games',
                    href: '/sport/commonwealth-games',
                },
            ]

            const headingText =
                location.pathname === '/sport' ? (
                    <StyledGradientSectionTitle>
                        Commonwealth Games
                    </StyledGradientSectionTitle>
                ) : (
                    <>
                        <Breadcrumb
                            items={commonwealthGamesHeaders}
                            enableHeaderTag={true}
                            verticalSpacing={'md'}
                        />
                    </>
                )

            return (
                <React.Fragment>
                    <StyledGradientWrap
                        linearGradientColors={['#021487', '#6E259B', '#BD37AC']}
                    >
                        <StyledFeatureContainer>
                            {headingText}
                            {!hideHeadingLink && (
                                <StyledFeatureSectionHeader
                                    heading="Full Coverage"
                                    headingUrl="/sport/commonwealth-games"
                                    showChevron
                                    colorOverride={'#fff'}
                                />
                            )}
                        </StyledFeatureContainer>
                        {styledThor}
                    </StyledGradientWrap>
                    {hasBorder && <StyledFeatureBorder />}
                </React.Fragment>
            )
        }
    }

    if (themeOverride === 'federal-election') {
        return (
            <StyledGradientWrap
                linearGradientColors={['#021487', '#6E259B', '#BD37AC']}
                boxShadow={true}
            >
                <StyledFeatureContainer>
                    {isHeadingH1 ? (
                        <StyledGradientSectionTitleH1>
                            Federal Election 2022
                        </StyledGradientSectionTitleH1>
                    ) : (
                        <StyledGradientSectionTitle>
                            Federal Election 2022
                        </StyledGradientSectionTitle>
                    )}

                    {!hideHeadingLink && (
                        <StyledFeatureSectionHeader
                            heading="Full Coverage"
                            headingUrl="/politics/federal-election"
                            showChevron={true}
                        />
                    )}
                </StyledFeatureContainer>
                {showPollieRaterBanner && (
                    <StyledPollieRaterBannerWrap>
                        <StyledPollieRaterBanner>
                            <StyledPollieRaterBannerTitle>
                                Rate the politicians running in the election!
                            </StyledPollieRaterBannerTitle>
                            <StyledPollieRaterBannerButton to="/politics/federal-election/pollie-rater">
                                Rate the politicians
                            </StyledPollieRaterBannerButton>
                        </StyledPollieRaterBanner>
                    </StyledPollieRaterBannerWrap>
                )}
                {styledThor}
            </StyledGradientWrap>
        )
    }

    return (
        <ThemeOverrider override={themeOverride}>{styledThor}</ThemeOverrider>
    )
}

Thor.displayName = 'ThorComposition'
